import type * as APITypes from '@viamrobotics/web-client/app/v1/app';

import type { ResourceTree } from './api';

export enum Role {
  ORGANIZATION_OWNER = 'organization_owner',
  ORGANIZATION_OPERATOR = 'organization_operator',
  ORGANIZATION_MEMBER = 'organization_member',
  LOCATION_OWNER = 'location_owner',
  LOCATION_OPERATOR = 'location_operator',
  LOCATION_MEMBER = 'location_member',
  ROBOT_OWNER = 'robot_owner',
  ROBOT_OPERATOR = 'robot_operator',
  ROBOT_MEMBER = 'robot_member',
}

export enum ResourceType {
  ORGANIZATION = 'organization',
  LOCATION = 'location',
  ROBOT = 'robot',
}

export type Resource =
  | APITypes.Organization
  | APITypes.Location
  | APITypes.Robot;

export type ResourceWithType =
  | { resource: APITypes.Organization; resourceType: ResourceType.ORGANIZATION }
  | { resource: APITypes.Location; resourceType: ResourceType.LOCATION }
  | { resource: APITypes.Robot; resourceType: ResourceType.ROBOT };

export type ResourceWithTypeAndRole = ResourceWithType & { roleName: RoleName };

export enum IdentityType {
  USER = 'user',
  API_KEY = 'api-key',
}

export const toReadableIdentityType = (type: IdentityType) => {
  return type === IdentityType.API_KEY ? 'API key' : type;
};

export interface IdentityWithName {
  id: string;
  type: IdentityType;
  name: string;
}

export type Permission =
  | 'read_organization'
  | 'write_organization'
  | 'read_fragment'
  | 'write_fragment'
  | 'read_location'
  | 'write_location'
  | 'read_robot'
  | 'read_robot_config'
  | 'write_robot'
  | 'control_robot'
  | 'read_organization_data_management'
  | 'read_location_data_management'
  | 'read_robot_data_management'
  | 'write_data_management'
  | 'read_location_secret'
  | 'read_robot_secret'
  | 'read_private_registry_item'
  | 'write_private_registry_item'
  | 'write_public_registry_item';

export const READ_ORGANIZATION: Permission = 'read_organization';
export const WRITE_ORGANIZATION: Permission = 'write_organization';

export const READ_FRAGMENT: Permission = 'read_fragment';
export const WRITE_FRAGMENT: Permission = 'write_fragment';

export const READ_LOCATION: Permission = 'read_location';
export const WRITE_LOCATION: Permission = 'write_location';

export const READ_ROBOT: Permission = 'read_robot';
export const READ_ROBOT_CONFIG: Permission = 'read_robot_config';
export const WRITE_ROBOT: Permission = 'write_robot';
export const CONTROL_ROBOT: Permission = 'control_robot';

export const READ_ORGANIZATION_DATA_MANAGEMENT: Permission =
  'read_organization_data_management';
export const READ_LOCATION_DATA_MANAGEMENT: Permission =
  'read_location_data_management';
export const READ_ROBOT_DATA_MANAGEMENT: Permission =
  'read_robot_data_management';
export const WRITE_DATA_MANAGEMENT: Permission = 'write_data_management';

export const READ_LOCATION_SECRET: Permission = 'read_location_secret';
export const READ_ROBOT_SECRET: Permission = 'read_robot_secret';

export const READ_PRIVATE_REGISTRY_ITEM: Permission =
  'read_private_registry_item';
export const WRITE_PRIVATE_REGISTRY_ITEM: Permission =
  'write_private_registry_item';
export const WRITE_PUBLIC_REGISTRY_ITEM: Permission =
  'write_public_registry_item';

export const ALL_PERMISSIONS = [
  READ_ORGANIZATION,
  WRITE_ORGANIZATION,

  READ_FRAGMENT,
  WRITE_FRAGMENT,

  READ_LOCATION,
  WRITE_LOCATION,

  READ_ROBOT,
  READ_ROBOT_CONFIG,
  WRITE_ROBOT,
  CONTROL_ROBOT,

  READ_ORGANIZATION_DATA_MANAGEMENT,
  READ_LOCATION_DATA_MANAGEMENT,
  READ_ROBOT_DATA_MANAGEMENT,
  WRITE_DATA_MANAGEMENT,

  READ_LOCATION_SECRET,
  READ_ROBOT_SECRET,

  READ_PRIVATE_REGISTRY_ITEM,
  WRITE_PRIVATE_REGISTRY_ITEM,
  WRITE_PUBLIC_REGISTRY_ITEM,
] as const;

// Role names for external display
export enum RoleName {
  OWNER = 'Owner',
  OPERATOR = 'Operator',
  MEMBER = 'Member',
}
// Pickers offer MEMBER through userRoleNames / apiKeyRoleNames, never from this list directly.
export const roleNames = [RoleName.OWNER, RoleName.OPERATOR] as const;

/**
 * Role names offered for a person. MEMBER needs ENABLE_ACCESS_POLICY: a Member
 * has no machine access until an access policy grants some.
 */
export const userRoleNames = (
  accessPolicyEnabled: boolean | undefined
): readonly RoleName[] =>
  accessPolicyEnabled ? [...roleNames, RoleName.MEMBER] : roleNames;

/**
 * Role names offered for an API key on a resource. Only machine-level keys are
 * rendered into a machine's auth, so a Member key anywhere else reaches nothing.
 */
export const apiKeyRoleNames = (
  accessPolicyEnabled: boolean | undefined,
  resourceType: ResourceType | undefined
): readonly RoleName[] =>
  resourceType === ResourceType.ROBOT
    ? userRoleNames(accessPolicyEnabled)
    : roleNames;

/**
 * Role names offered for a key made on the machine page. A machine's auth
 * carries only its owner and member keys, so Operator isn't offered here.
 */
export const machineKeyRoleNames = (
  accessPolicyEnabled: boolean | undefined
): readonly RoleName[] =>
  accessPolicyEnabled ? [RoleName.OWNER, RoleName.MEMBER] : [RoleName.OWNER];

export const toRole = (
  roleName: RoleName,
  resourceType: ResourceType
): Role => {
  return `${resourceType}_${roleName}`.toLowerCase() as Role;
};

/** Converts a role to its role name (owner, operator) */
export const toRoleName = (role: Role | undefined): RoleName | undefined => {
  if (!role) {
    return;
  }
  const underscoreIndex = role.indexOf('_');
  if (underscoreIndex === -1) {
    return;
  }
  let roleName = role.slice(underscoreIndex + 1);
  roleName = roleName.charAt(0).toUpperCase() + roleName.slice(1);

  return roleName as RoleName;
};

/** Returns whether a role is an owner role */
export const isOwnerRole = (role: Role | undefined) => {
  return (
    role === Role.ORGANIZATION_OWNER ||
    role === Role.LOCATION_OWNER ||
    role === Role.ROBOT_OWNER
  );
};

/** Returns the write permission for a resource type */
export const getWritePermission = (
  resourceType: ResourceType | undefined
): Permission | undefined => {
  switch (resourceType) {
    case ResourceType.ORGANIZATION: {
      return WRITE_ORGANIZATION;
    }
    case ResourceType.LOCATION: {
      return WRITE_LOCATION;
    }
    case ResourceType.ROBOT: {
      return WRITE_ROBOT;
    }
  }
  return undefined;
};

/**
 * Returns the user's role for a resource in the org
 * @param resourceID the resource of the role to find
 * @param authorizations a user's authorizations
 * @returns the role name if the user has a role on the resource, undefined if not
 */
export const getResourceRoleName = (
  resourceID: string,
  authorizations: APITypes.Authorization[] | APITypes.AuthorizationDetails[]
): RoleName | undefined => {
  return toRoleName(
    authorizations.find((auth) => auth.resourceId === resourceID)
      ?.authorizationId as Role
  );
};

/**
 * Returns the org member's highest authorization, where highest authorization represents the member's
 * highest priority authorization as defined by rolePriorities
 * @param userID the user ID of the member
 * @param authsByIdentityId all member authorizations in the org, indexed by user ID
 * @returns the highest authorization of the member. undefined if the member has no authorizations in the org
 */
export const getHighestAuth = (
  userID: string | undefined,
  authsByIdentityId: Record<string, APITypes.Authorization[]>
): APITypes.Authorization | undefined => {
  // authorizations in authsByIdentityId are sorted by priority order
  return userID ? authsByIdentityId[userID]?.[0] : undefined;
};

/**
 * Returns a user's highest owner role.
 * @param userID the user ID of the member
 * @param authsByIdentityId all member authorizations in the org, indexed by user ID
 * @returns an owner authorization if the user has at least 1 owner role, undefined otherwise.
 */
export const getHighestOwnerRole = (
  userID: string | undefined,
  authsByIdentityId: Record<string, APITypes.Authorization[]>
): APITypes.Authorization | undefined => {
  // authorizations in authsByIdentityId are sorted by priority order
  if (!userID) {
    return undefined;
  }
  const userAuths = authsByIdentityId[userID];
  if (!userAuths) {
    return undefined;
  }
  for (const auth of userAuths) {
    if (isOwnerRole(auth.authorizationId as Role)) {
      return auth;
    }
  }
  return undefined;
};

/**
 * `authorizerUserId` and `receiverIdentity` identify the authorizerUser and receiverIdentity respectively.
 *
 * Returns if authorizerUser can grant authorizations that expand the permissions of receiverIdentity.
 *
 * For any resource, if authorizerUser has owner permissions (inherited or explicit) on it
 * and receiverIdentity is not an owner of it and receiverIdentity doesn't have robot_operator on it,
 * then authorizerUser can grant additional permissions.
 *
 * This function performs BFS on the resource tree, starting from the organization level. It looks
 * for resources which satisfy the condition above.
 */
export const canExpandPermissions = (
  organizationId: string | undefined,
  authorizerUserId: string | undefined,
  receiverIdentity: IdentityWithName,
  resourceTree: ResourceTree,
  authsByResourceID: Record<string, APITypes.Authorization[]>
) => {
  if (!organizationId) {
    return false;
  }
  if (!authorizerUserId || authorizerUserId === receiverIdentity.id) {
    return false;
  }
  const resourceQueue: string[] = [organizationId];
  let currentResource: string | undefined;
  while ((currentResource = resourceQueue.shift())) {
    // find authorizer and receiver user authorizations on this resource
    const authorizerAuthz = findAuth(
      authorizerUserId,
      IdentityType.USER,
      authsByResourceID[currentResource]
    );
    const receiverAuthz = findAuth(
      receiverIdentity.id,
      receiverIdentity.type,
      authsByResourceID[currentResource]
    );

    /*
     * If the authorizer owns the resource and the receiver has no authorizations
     * on it, then the authorizer can grant permissions on the resource and any
     * sub resources.
     * If the authorizer owns the resource and the receiver is an operator on it,
     * then the authorizer can grant permissions on sub resources (except if the
     * resource is a robot, which has no sub resources).
     */
    if (
      isOwnerRole(authorizerAuthz?.authorizationId as Role) &&
      !isOwnerRole(receiverAuthz?.authorizationId as Role) &&
      (receiverAuthz?.authorizationId as Role) !== Role.ROBOT_OPERATOR
    ) {
      return true;
    }
    if (!isOwnerRole(receiverAuthz?.authorizationId as Role)) {
      // add children to search if props user doesn't own anything down this branch yet
      const children = resourceTree[currentResource]?.childrenIDs ?? [];
      resourceQueue.push(...children);
    }
  }
  return false;
};

const findAuth = (
  identityId: string,
  identityType: string,
  auths: APITypes.Authorization[] = []
): APITypes.Authorization | undefined => {
  return auths.find(
    (auth) =>
      auth.identityId === identityId && auth.identityType === identityType
  );
};
